// disable useContraMutation eslint error

import {
  type PaymentMethodType,
  type useCreateBillingMethodClientSecretMutation,
  type useCreateBillingMethodClientSecretMutation$data,
} from '#app/__generated__/relay/useCreateBillingMethodClientSecretMutation.graphql.js';
import {
  useCreateBillingMethodRefreshSetupIntentAndAttachPaymentMethodIfReadyMutation,
  useCreateBillingMethodRefreshSetupIntentAndAttachPaymentMethodIfReadyMutation$data,
} from '#app/__generated__/relay/useCreateBillingMethodRefreshSetupIntentAndAttachPaymentMethodIfReadyMutation.graphql.js';
import { type CardDetailsInput } from '#app/types/wallet.js';
import { graphql, useMutation } from '@contra/relay';
import {
  type SetupIntentResult,
  type Stripe,
  type StripeCardNumberElement,
} from '@stripe/stripe-js';
import { useCallback } from 'react';

const saveBillingMethodToStripe = ({
  card,
  clientSecret,
  formData,
  stripe,
}: {
  card: StripeCardNumberElement;
  clientSecret: string;
  formData: CardDetailsInput;
  stripe: Stripe;
}): Promise<SetupIntentResult> =>
  stripe.confirmCardSetup(clientSecret, {
    payment_method: {
      billing_details: {
        address: {
          city: formData.billingCity,
          country: formData.country,
          line1: formData.billingAddress,
          postal_code: formData.billingZip,
          state: formData.billingState,
        },
        name: formData.nameOnCard,
      },
      card,
    },
  });

export const useCreateBillingMethod = () => {
  const [generateSetupIntentToken] =
    useMutation<useCreateBillingMethodClientSecretMutation>(graphql`
      mutation useCreateBillingMethodClientSecretMutation(
        $input: GenerateStripeSetupIntentClientSecretInput!
      ) {
        generateStripeSetupIntentClientSecret(input: $input) {
          clientSecret
          errors {
            message
          }
        }
      }
    `);

  const createStripeClientSecret = useCallback(
    (paymentMethodType: PaymentMethodType): Promise<string> => {
      return new Promise((resolve, reject) => {
        generateSetupIntentToken({
          onCompleted: (
            response: useCreateBillingMethodClientSecretMutation$data,
          ) => {
            const {
              generateStripeSetupIntentClientSecret: { clientSecret, errors },
            } = response;

            if (errors?.[0] || !clientSecret) {
              const errorMessage =
                errors?.[0]?.message ??
                'An error occurred attempting to generate a stripe client secret.';
              reject(new Error(errorMessage));
            } else {
              resolve(clientSecret);
            }
          },
          onError: reject,
          variables: {
            input: {
              paymentMethodType,
            },
          },
        });
      });
    },
    [generateSetupIntentToken],
  );

  const [refreshSetupIntentAndAttachPaymentMethodIfReadyMutation] =
    useMutation<useCreateBillingMethodRefreshSetupIntentAndAttachPaymentMethodIfReadyMutation>(
      graphql`
        mutation useCreateBillingMethodRefreshSetupIntentAndAttachPaymentMethodIfReadyMutation(
          $input: RefreshSetupIntentAndAttachPaymentMethodIfReadyInput!
        ) {
          refreshSetupIntentAndAttachPaymentMethodIfReady(input: $input) {
            visitor {
              userAccount {
                ...WalletPayoutAccountsFragment
                ...PaymentMethodsListFragment_paymentMethodsV2
                paymentMethods {
                  edges {
                    node {
                      ... on StripeCard {
                        id
                        label
                        workspacePaymentMethodId
                        brand
                        cardStatus: status
                        isDefault
                        canDelete
                        last4
                      }
                      ... on StripeUsBankAccountPaymentMethod {
                        id
                        label
                        workspacePaymentMethodId
                        bankName
                        isDefault
                        last4
                      }
                      ... on StripeSepaDebitPaymentMethod {
                        id
                        label
                        workspacePaymentMethodId
                        isDefault
                        lastFourDigits
                      }
                    }
                  }
                }
                stripeAccount {
                  ...StripeContextProviderFragment
                }
              }
            }
            errors {
              message
            }
          }
        }
      `,
    );

  const refreshSetupIntentAndAttachPaymentMethodIfReady = useCallback(
    ({
      foreignStripeSetupIntentId,
      visibilityToAttachAs,
    }: {
      foreignStripeSetupIntentId: string;
      visibilityToAttachAs: 'PRIVATE' | 'WORKSPACE';
    }): Promise<useCreateBillingMethodRefreshSetupIntentAndAttachPaymentMethodIfReadyMutation$data> => {
      return new Promise((resolve, reject) => {
        refreshSetupIntentAndAttachPaymentMethodIfReadyMutation({
          onCompleted: (
            response: useCreateBillingMethodRefreshSetupIntentAndAttachPaymentMethodIfReadyMutation$data,
          ) => {
            const {
              refreshSetupIntentAndAttachPaymentMethodIfReady: { errors },
            } = response;

            if (errors?.[0]) {
              const errorMessage =
                errors?.[0]?.message ??
                'An error occurred attempting to attach payment method.';
              reject(new Error(errorMessage));
            } else {
              resolve(response);
            }
          },
          onError: reject,
          variables: {
            input: {
              foreignStripeSetupIntentId,
              visibilityToAttachAs,
            },
          },
        });
      });
    },
    [refreshSetupIntentAndAttachPaymentMethodIfReadyMutation],
  );

  return {
    createStripeClientSecret,
    refreshSetupIntentAndAttachPaymentMethodIfReady,
    saveBillingMethodToStripe,
  };
};
